import { createQueryKeys } from '@lukemorales/query-key-factory';
import {
  keepPreviousData,
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { HttpService } from '@/features/api/http.service';
import { notifyError } from '@/libs/bugsnag';
import type {
  ICreateNotionSyncResponse,
  IImportJobOutcome,
  IPaginatedZones,
  IZone,
  IZoneFilterOptions,
  IZoneListFilter,
} from '@/types/api';
import { isUnexpectedZonesError } from '../utils/mapZonesApiError';

const ZONES_PATH = '/zones';
const ZONES_FILTERS_PATH = '/zones/filters';
const ZONES_FACTORY_NAME = 'zones';

/**
 * Build the query string for the read-only Zone Management list.
 * Arrays serialise as repeated singular keys (`name=A&name=B`, `country=FR&country=IT`)
 * to match the OpenAPI `style: form, explode: true` `ListZones`
 * parameter names. There is no `status` filter — hunting zones carry
 * no SpotOn workflow status.
 */
function serializeZoneListFilter(filter: IZoneListFilter): URLSearchParams {
  const params = new URLSearchParams();
  if (filter.names) {
    for (const n of filter.names) params.append('name', n);
  }
  if (filter.countries) {
    for (const c of filter.countries) params.append('country', c);
  }
  if (filter.owner_ids) {
    for (const id of filter.owner_ids) params.append('owner_id', id);
  }
  if (filter.page !== undefined) params.set('page', String(filter.page));
  if (filter.limit !== undefined) params.set('limit', String(filter.limit));
  return params;
}

/**
 * Read-side query keys for the hunting-zones feature.
 * `list` is the read-only dashboard; `importOutcome` is the terminal
 * per-row outcome. Live progress is NOT a query — it streams over SSE
 * via `useImportJobStream`.
 */
export const zonesKeyFactory = createQueryKeys(ZONES_FACTORY_NAME, {
  list: (filter: IZoneListFilter) => ({
    queryKey: [ZONES_PATH, filter],
    queryFn: () =>
      HttpService.get<string, IPaginatedZones>(ZONES_PATH, serializeZoneListFilter(filter)),
  }),
  importOutcome: (jobId: string) => ({
    queryKey: [ZONES_PATH, 'imports', 'jobs', jobId, 'outcome'],
    queryFn: () =>
      HttpService.get<string, IImportJobOutcome>(`${ZONES_PATH}/imports/jobs/${jobId}/outcome`),
  }),
  /**
   * Single hunting zone by id. Used by `MapSurface`
   * to hydrate the anchored zone-detail bubble. Unexpected failures (5xx /
   * transport) report to Bugsnag and rethrow; expected 404s (zone no longer
   * exists) are user-actionable and not reported.
   */
  byId: (zoneId: string) => ({
    queryKey: [ZONES_PATH, zoneId],
    queryFn: async (): Promise<IZone> => {
      try {
        return await HttpService.get<string, IZone>(`${ZONES_PATH}/${zoneId}`);
      } catch (error: unknown) {
        if (isUnexpectedZonesError(error)) {
          notifyError(error, { context: 'zones:byId' });
        }
        throw error;
      }
    },
  }),
  /**
   * Option values backing the Zone Management index's owner filter and the
   * map search bar's zone-name catalogue. Takes no parameters and is not
   * page-scoped, so one fetch per session serves every page — which is the
   * point: options derived from the current page would change as the BD
   * paginates.
   */
  filterOptions: () => ({
    queryKey: [ZONES_FILTERS_PATH],
    queryFn: async (): Promise<IZoneFilterOptions> => {
      try {
        return await HttpService.get<string, IZoneFilterOptions>(ZONES_FILTERS_PATH);
      } catch (error: unknown) {
        if (isUnexpectedZonesError(error)) {
          notifyError(error, { context: 'zones:filterOptions' });
        }
        throw error;
      }
    },
  }),
});

/**
 * Invalidate every cached hunting-zones query. Called after a sync so
 * the read-only dashboard and any job views refetch in one round-trip.
 */
export async function invalidateAllZoneQueries(qc: QueryClient): Promise<void> {
  await qc.invalidateQueries({ queryKey: [ZONES_FACTORY_NAME] });
}

// ── Read hooks ─────────────────────────────────────────────────────────────

export function useZonesQuery(filter: IZoneListFilter) {
  return useQuery({ ...zonesKeyFactory.list(filter), placeholderData: keepPreviousData });
}

/**
 * Fetch the option values for the zone filters. The filter bar stays usable
 * when this fails — the owner popover then only offers "Unassigned" — so a
 * single attempt is enough, same rationale as `useZoneByIdQuery`.
 */
export function useZoneFilterOptionsQuery() {
  return useQuery({ ...zonesKeyFactory.filterOptions(), retry: false });
}

/**
 * Fetch a single hunting zone by id. Used by
 * `MapSurface` to hydrate the anchored zone-detail bubble. The query stays
 * disabled when `zoneId` is empty so callers can safely pass an empty
 * string before the id is known (e.g. while routing is resolving).
 */
export function useZoneByIdQuery(zoneId: string) {
  return useQuery({
    ...zonesKeyFactory.byId(zoneId),
    enabled: Boolean(zoneId),
    // The app's QueryClient sets no retry override, so TanStack's default
    // (retry: 3) would turn one 404 into four requests and delay the failure
    // toast — the only feedback a failed zone gets — by the full backoff.
    retry: false,
    // Switching zones swaps the detail panel's contents instead of closing and reopening it: the
    // panel renders on `data`, so the gap while the next zone loads would unmount it — and only
    // for a zone that is not already cached, which made the two behaviours look random.
    placeholderData: keepPreviousData,
  });
}

/**
 * Terminal per-row outcome of a Notion sync job. Returns 409 while the
 * job is still in progress — gate `enabled` on a terminal status so the
 * outcome screen only fetches once the SSE stream has terminated.
 */
export function useImportJobOutcomeQuery(jobId: string, enabled = true) {
  return useQuery({
    ...zonesKeyFactory.importOutcome(jobId),
    enabled: enabled && Boolean(jobId),
  });
}

// ── Mutations — Notion sync ────────────────────────────────────────────────

/**
 * Start an auto-commit Notion sync.
 *
 * The endpoint takes no parameters — the Notion data source is configured
 * server-side. We POST with no body (the operation's request body is
 * optional; a non-empty body is rejected with 400). On `202 Accepted`
 * the response carries the new job's id + initial `queued` status; the
 * caller navigates to the outcome screen and subscribes to the SSE
 * progress stream via `useImportJobStream`.
 *
 * Open to any authenticated user. Map thrown errors through
 * `mapZonesApiError(err, { source: 'sync' })`.
 */
export function useCreateNotionSyncMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (): Promise<ICreateNotionSyncResponse> =>
      HttpService.post<undefined, ICreateNotionSyncResponse>(
        `${ZONES_PATH}/imports/notion`,
        undefined,
      ),
    onSuccess: async () => {
      await invalidateAllZoneQueries(queryClient);
    },
  });
}
